/**
 * The four knots of diagonal-knot ("normal") friendship bracelet patterns.
 *
 * Every knot is tied between two ADJACENT strings. One of the two is the
 * "working" string: it wraps around the other (the "holding" string). The
 * working string's colour is what you see, because it is the thread that
 * actually forms the visible bump of the knot.
 *
 * Each of the four knots is built from one or two half-hitch pairs:
 *
 *   forward  (F, drawn `/`)  left string  hitches over the right, TWICE.
 *                            The left string ends up on the right.
 *                            -> shows the LEFT colour, strings SWAP.
 *
 *   backward (B, drawn `\`)  right string hitches over the left, TWICE.
 *                            The right string ends up on the left.
 *                            -> shows the RIGHT colour, strings SWAP.
 *
 *   forward-backward (FB, drawn `>`)
 *                            a forward knot, then a backward knot. The left
 *                            string travels right and then comes straight back.
 *                            -> shows the LEFT colour, strings STAY.
 *
 *   backward-forward (BF, drawn `<`)
 *                            a backward knot, then a forward knot. The right
 *                            string travels left and then comes straight back.
 *                            -> shows the RIGHT colour, strings STAY.
 *
 * Why FB shows the LEFT colour: the first (forward) half carries the left
 * string to the right-hand position, so the second (backward) half is tied by
 * that same string, now sitting on the right. Both visible hitches therefore
 * belong to the original left string. BF is the mirror of this argument.
 *
 * This single table is the load-bearing fact of the whole normal-pattern
 * engine, so it is expressed once, here, and derived everywhere else.
 */
export type KnotType = 'F' | 'B' | 'FB' | 'BF';

export const KNOT_TYPES: readonly KnotType[] = ['F', 'B', 'FB', 'BF'] as const;

export interface KnotBehaviour {
  /** Which of the two strings is visible: the left one or the right one. */
  readonly shows: 'left' | 'right';
  /** Whether the two strings trade places after the knot. */
  readonly swaps: boolean;
  /** How many half-hitch pairs the crafter ties (1 or 2). */
  readonly hitchPairs: 1 | 2;
  /** Conventional chart glyph. */
  readonly glyph: string;
  /** Human name used in generated instructions. */
  readonly name: string;
  /** One-line physical instruction. */
  readonly howTo: string;
}

export const KNOT_BEHAVIOUR: Readonly<Record<KnotType, KnotBehaviour>> = {
  F: {
    shows: 'left',
    swaps: true,
    hitchPairs: 1,
    glyph: '/',
    name: 'forward knot',
    howTo:
      'Take the LEFT string, make a "4" shape over the right string, pass the tail through and pull up. Do it twice. The left string is now on the right.',
  },
  B: {
    shows: 'right',
    swaps: true,
    hitchPairs: 1,
    glyph: '\\',
    name: 'backward knot',
    howTo:
      'Take the RIGHT string, make a backwards "4" over the left string, pass the tail through and pull up. Do it twice. The right string is now on the left.',
  },
  FB: {
    shows: 'left',
    swaps: false,
    hitchPairs: 2,
    glyph: '>',
    name: 'forward-backward knot',
    howTo:
      'Take the LEFT string and tie a forward knot, then tie a backward knot with that same string to bring it home. It ends up back on the left.',
  },
  BF: {
    shows: 'right',
    swaps: false,
    hitchPairs: 2,
    glyph: '<',
    name: 'backward-forward knot',
    howTo:
      'Take the RIGHT string and tie a backward knot, then tie a forward knot with that same string to bring it home. It ends up back on the right.',
  },
} as const;

/** True when the knot leaves the two strings in swapped positions. */
export function knotSwaps(knot: KnotType): boolean {
  return KNOT_BEHAVIOUR[knot].swaps;
}

/**
 * Which of the two strand slots supplies the visible colour.
 * Returns 0 for the left-hand string, 1 for the right-hand string.
 */
export function visibleSlot(knot: KnotType): 0 | 1 {
  return KNOT_BEHAVIOUR[knot].shows === 'left' ? 0 : 1;
}

/**
 * Apply a knot to a (left, right) pair of strand ids.
 * Returns the visible strand id plus the pair's new left/right occupants.
 */
export function applyKnot(
  knot: KnotType,
  left: number,
  right: number,
): { shown: number; left: number; right: number } {
  const shown = KNOT_BEHAVIOUR[knot].shows === 'left' ? left : right;
  return KNOT_BEHAVIOUR[knot].swaps
    ? { shown, left: right, right: left }
    : { shown, left, right };
}

/**
 * Choose the knot that shows `wantStrand` and produces the requested swap.
 * Returns null when the request is impossible (the wanted strand is not in the
 * pair), which callers treat as a design error rather than silently fudging.
 */
export function chooseKnot(
  left: number,
  right: number,
  wantStrand: number,
  wantSwap: boolean,
): KnotType | null {
  if (wantStrand === left) return wantSwap ? 'F' : 'FB';
  if (wantStrand === right) return wantSwap ? 'B' : 'BF';
  return null;
}

/** Number of half-hitches (individual loops) the crafter physically ties. */
export function halfHitchCount(knot: KnotType): number {
  // Each "hitch pair" in bracelet parlance is two loops of the working thread.
  return KNOT_BEHAVIOUR[knot].hitchPairs * 2;
}
